import { IVesselVisit, IVesselVisitGridItem } from "./model";
import { IVesselActivity, VesselActivityType } from "./model";
import { IVesselMovement } from "../vessel-movement";
import { ILocationForNOA, LocationTypeForNOA } from "@pmis3/master-data-core";
import { IVessel } from "@pmis3/master-data-core";
import { IVesselStop } from "../vessel-stop";
import { IOrganizationType } from "@pmis3/master-data-core";

export class PPAObjectMappingHelper {
    static mapVesselVisitToVesselVisitDTO(vv: IVesselVisit) {
        const vas = vv.vesselActivities ?? [];
        const mergedVesselActivities = this.mergeVesselActivitiesOrdered(vas);

        const {
            vessel,
            lastPortOfCall,
            nextPortOfCall,
            vesselActivities,
            vesselId,
            lastPortOfCallId,
            nextPortOfCallId,
            ...rest
        } = vv;

        return {
            ...rest,
            vesselId: vesselId,
            lastPortOfCallId: lastPortOfCallId,
            nextPortOfCallId: nextPortOfCallId,
            stops: mergedVesselActivities
        };
    }

    static mergeVesselActivitiesOrdered(activities: IVesselActivity[]) {
        const merged = [];

        let i = 0;
        while (i < activities.length) {
            const movementIndex = i;
            const stopIndex = i + 1;
            const movement = activities[movementIndex].activityType == VesselActivityType.Movement ? activities[movementIndex] : null;
            const stop = stopIndex === activities.length ? null : (
                activities[stopIndex].activityType == VesselActivityType.Stop ? activities[stopIndex] : null
            );
            // const estimatedStartTime = movement?.estimatedEndTime;
            // const startTime = movement?.endTime;
            // const estimatedEndTime = stop?.estimatedEndTime;
            // const endTime = stop?.endTime;

            if (movement && stop && movement.id === stop.id) {
                const locationId = stop.startLocationId;
                const tripId = movement.vesselVisitId == stop.vesselVisitId ? movement.vesselVisitId : null;

                const {
                    startLocationId: _mStartId,
                    endLocationId: _mEndId,
                    startLocation: _mStart,
                    endLocation: _mEnd,
                    estimatedEndTime: estimatedStartTime,
                    endTime: startTime,
                    ...cleanMovement
                } = movement;

                const {
                    startLocationId: _sStartId,
                    endLocationId: _sEndId,
                    startLocation: _sStart,
                    endLocation: _sEnd,
                    estimatedEndTime: estimatedEndTime,
                    endTime: endTime,
                    serviceApplications: _sApps,
                    ...cleanStop
                } = stop;

                merged.push({
                    ...cleanMovement,
                    ...cleanStop,
                    locationId: locationId,
                    startEstimatedTime: estimatedStartTime,
                    startTime: startTime,
                    endEstimatedTime: estimatedEndTime,
                    endTime: endTime,
                    applications: _sApps,
                    tripId: tripId
                });

                i += 2;
            } else {
                if (movement) {
                    const {
                        startLocationId,
                        endLocationId,
                        vesselVisitId,
                        ...rest
                    } = movement;

                    merged.push({
                        ...rest,
                        startLocationId: startLocationId,
                        endLocationId: endLocationId,
                        // startEstimatedTime: estimatedStartTime,
                        // startTime: startTime,
                        // endEstimatedTime: estimatedEndTime,
                        tripId: vesselVisitId
                    });
                }
                i += 1;
            }
        }
        return merged;
    }

    static mapVesselVisitDTOToVesselVisit(dto: any, locations: ILocationForNOA[], vessels: IVessel[]): IVesselVisit {
        const {
            vesselId,
            lastPortOfCallId,
            nextPortOfCallId,
            stops,
            ...rest
        } = dto;

        const vesselActivities: IVesselActivity[] = [];

        let previousStopLocation: ILocationForNOA | undefined;

        for (const stop of stops ?? []) {
            const {
                locationId,
                startEstimatedTime,
                endEstimatedTime,
                startTime,
                endTime,
                applications,
                maxDisplacement,
                aftDraft,
                foreDraft,
                midDraft,
                airDraft,
                loadedStatus,
                vesselOrientation,
                purposes,
                cargoes,
                tripId,
                statusId,
                tugOrganizationId,
                launchOrganizationId,
                ...restStop
            } = stop;

            const location = locations.find(location => location.id == locationId);
            const isNotLastStop = !!location;
            previousStopLocation = location;

            // Recreate movement
            vesselActivities.push({
                ...restStop,
                startLocationId: previousStopLocation?.id,
                startLocation: previousStopLocation,
                endLocationId: location?.id,
                endLocation: location,
                estimatedEndTime: startEstimatedTime, // original movement.endTime
                endTime: startTime,
                activityType: VesselActivityType.Movement,
                maxDisplacement: maxDisplacement,
                aftDraft: aftDraft,
                foreDraft: foreDraft,
                midDraft: midDraft,
                airDraft: airDraft,
                loadedStatus: loadedStatus,
                cargoes: cargoes,
                vesselVisitId: tripId,
                statusId: statusId,
                tugOrganizationId: tugOrganizationId,
                launchOrganizationId: launchOrganizationId
            } as IVesselMovement);

            // Recreate stop
            if (isNotLastStop) {
                vesselActivities.push({
                    ...restStop,
                    startLocationId: locationId,
                    startLocation: location,
                    estimatedEndTime: endEstimatedTime, // original stop.endTime
                    endTime: endTime,
                    serviceApplications: applications,
                    activityType: VesselActivityType.Stop,
                    vesselOrientation: vesselOrientation,
                    purposes: purposes,
                    vesselVisitId: tripId
                } as IVesselStop);
            }
        }

        const vessel = vessels.find(vessel => vessel.id == vesselId);

        return {
            ...rest,
            vesselId: vesselId,
            vessel: vessel,
            lastPortOfCallId: lastPortOfCallId,
            nextPortOfCallId: nextPortOfCallId,
            vesselActivities
        } as IVesselVisit;
    }

    static mapVesselVisitGridDTOToVesselVisitGrid(dto: any, locations: ILocationForNOA[]): IVesselVisitGridItem {
        const {
            stops,
            ...rest
        } = dto;
        const mappedVesselActivitiesFromStops: IVesselActivity[] = []

        let previousStopLocation;

        for (const stop of stops ?? []) {
            const currentLocation = locations.find(location => location.id == stop.locationId);
            const isNotLastStop = !!currentLocation;

            const movement = {
                activityType: VesselActivityType.Movement,
                id: stop.id,
                estimatedEndTime: stop.startEstimatedTime,
                endTime: stop.startTime,
                startLocation: previousStopLocation,
                endLocation: currentLocation,
                statusId: stop.statusId
            } as IVesselMovement
            mappedVesselActivitiesFromStops.push(movement)

            if (isNotLastStop) {
                const vesselStop = {
                    activityType: VesselActivityType.Stop,
                    id: stop.id,
                    estimatedEndTime: stop.endEstimatedTime,
                    endTime: stop.endTime,
                    startLocation: currentLocation
                } as IVesselStop
                mappedVesselActivitiesFromStops.push(vesselStop)
            }
            previousStopLocation = currentLocation;
        }

        return {
            ...rest,
            vesselActivities: mappedVesselActivitiesFromStops
        } as IVesselVisitGridItem
    }

    static mapOrganizationTypeDTOToOrganizationType(dto: any) {
        return {
            organizationTypeId: dto.id,
            organizationTypeName: dto.displayName
        } as IOrganizationType;
    }

    static mapVesselDTOToVessel(dto: any) {
        const {
            callsign,
            portOfRegisty,
            ...rest
        } = dto;

        const vessel = {
            ...rest,
            callSign: callsign,
            portOfRegistyName: portOfRegisty,
        } as IVessel

        return vessel;
    }

    static filterLocationsForNOA(locations: ILocationForNOA[]): ILocationForNOA[] {
        const terminalIds = Object.values(PPATerminalIds).filter(terminal => typeof terminal === "number") as number[];
        const pilotStationIds = Object.values(PPAPilotStationIds).filter(pilotStation => typeof pilotStation === "number") as number[];

        const terminalAndPilotStationIds = [
            ...terminalIds,
            ...pilotStationIds
        ];

        return locations.filter(location =>
            terminalAndPilotStationIds.includes(location.id) ||
            location.parentId === PPATerminalIds.EnglishBayAnchorages ||
            location.parentId === PPATerminalIds.IndianArm ||
            location.parentId === PPATerminalIds.Westridge
        );
    }

    static mapBerthsForNOA(locations: ILocationForNOA[]): ILocationForNOA[] {
        return locations.filter(location => 
            location.id == 1480 ||
            location.id == 1481 ||
            location.id == 1482 ||
            location.id == 29 ||
            location.id == 30 ||
            location.id == 31 ||
            location.id == 32 ||
            location.parentId == PPATerminalIds.EnglishBayAnchorages
        ).map(location => ({
            ...location,
            locationType: LocationTypeForNOA.Berth
        })).sort((a, b) => a.name.localeCompare(b.name));
    }

    static filterTerminalsAndPilotStationDropDown(locations: ILocationForNOA[]): ILocationForNOA[] {
        return [...this.mapTerminalsForNOA(locations), ...this.mapPilotStationsForNOA(locations)].sort((a, b) => a.name.localeCompare(b.name));
    }

    // static filterBerthsAndPilotStationsDropDown(locations: ILocationForNOA[]): ILocationForNOA[] {
    //     return [...this.mapBerthsForNOA(locations), ...this.mapPilotStationsForNOA(locations)]
    // }

    static mapTerminalsForNOA(locations: ILocationForNOA[]): ILocationForNOA[] {
        return locations
            .filter(location =>
                location.id === PPATerminalIds.Westridge ||
                location.id === PPATerminalIds.IndianArm ||
                location.id === PPATerminalIds.ANA ||
                location.id === PPATerminalIds.FRN ||
                location.id === PPATerminalIds.SFMT ||
                location.id === PPATerminalIds.EnglishBayAnchorages
            )
            .map(location => {
                let locationName = '';
                if (location.id == PPATerminalIds.Westridge) {
                    locationName = 'WESTRIDGE TERMINAL';
                } else if (location.id == PPATerminalIds.IndianArm) {
                    locationName = 'INDIAN ARM';
                }

                return {
                    ...location,
                    name: locationName || location.name,
                    locationType: LocationTypeForNOA.Terminal
                };
            });
    }

    static mapPilotStationsForNOA(locations: ILocationForNOA[]): ILocationForNOA[] {
        // brotchie and ebz
        return locations
            .filter(location =>
                location.id === PPAPilotStationIds.Brotchie ||
                location.id === PPAPilotStationIds.EBZ ||
                location.id === PPAPilotStationIds.TAZ ||
                location.id === PPAPilotStationIds.QAZ ||
                location.id === PPAPilotStationIds.REZ ||
                location.id === PPAPilotStationIds.RSZ ||
                location.id === PPAPilotStationIds.PatosIsland
            )
            .map(location => ({
                ...location,
                locationType: LocationTypeForNOA.PilotStation
            }));
    }
}

enum PPATerminalIds {
    Westridge               = 60,   // Westridge Terminal
    IndianArm               = 141,  // Indian Arm
    ANA                     = 394,  // ANACORTES WA
    FRN                     = 422,  // FERNDALE WA
    EnglishBayAnchorages    = 139,  // ENGLISH BAY ANCHORAGE
    SFMT                    = 644   // SOUTH FRASER MARINE TERMINAL
}

enum PPAPilotStationIds {
    Brotchie    = 251,  // BROTCHIE
    EBZ         = 443,  // ENGLISH BAY PILOT CHANGE
    PatosIsland = 512,  // PATOS ISLAND
    TAZ         = 1495, // TANGO ALPHA PILOT CHANGE
    QAZ         = 1496, // QUEBEC ALPHA PILOT CHANGE
    RSZ         = 1497, // RACE ROCKS SOUTH
    REZ         = 1498, // RACE ROCKS EAST
}